import React, { useState, useEffect } from "react";
import { useAppDispatch } from "@/hooks/redux";
import { createPlan, updatePlan, deletePlan } from "@/store/slices/adminSlice";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import {
  Plus,
  Minus,
  Star,
  AlertTriangle,
  Code2,
  LibraryBig,
  AppWindowMac,
  Download,
} from "lucide-react";
import { toast } from "sonner";
import type {
  AdminPlan,
  AdminCreatePlanRequest,
} from "@/services/adminService";
import stripeService from "@/services/stripeService";
import type { StripeProduct } from "@/services/stripeService";
import type { StripePrice } from "@/services/stripeService";
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from "@/components/ui/select";
import adminService from "@/services/adminService";
import { getFeaturesByCategory } from "@/types/features";

// Create Plan Dialog
interface CreatePlanDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export const CreatePlanDialog: React.FC<CreatePlanDialogProps> = ({
  open,
  onOpenChange,
  onSuccess,
}) => {
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<AdminCreatePlanRequest>({
    name: "",
    description: "",
    features: [""],
    billingInterval: "month",
    discountPercentage: null,
    enabledFeatures: [],
    isActive: true,
    isPopular: false,
    badge: "",
    badgeColor: "",
    displayOrder: 1,
    productId: undefined,
    priceId: undefined,
  });
  const [products, setProducts] = useState<StripeProduct[]>([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [plans, setPlans] = useState<AdminPlan[]>([]);
  const [plansLoading, setPlansLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setProductsLoading(true);
      setPlansLoading(true);
      Promise.all([stripeService.getProducts(), adminService.listPlans()])
        .then(([prods, plans]) => {
          setProducts(prods);
          setPlans(plans);
        })
        .catch(() => {
          setProducts([]);
          setPlans([]);
        })
        .finally(() => {
          setProductsLoading(false);
          setPlansLoading(false);
        });
    }
  }, [open]);

  // Filter out products already assigned to a plan
  const assignedProductIds = new Set(
    (plans || []).map((plan) => plan.productId).filter(Boolean),
  );
  const availableProducts = products.filter(
    (product) =>
      !assignedProductIds.has(product.id) || formData.productId === product.id,
  );

  // Auto-fetch priceId when product is selected
  const handleProductChange = async (productId: string) => {
    setFormData((prev) => ({ ...prev, productId, priceId: undefined }));

    if (productId) {
      try {
        const prices = await stripeService.getPricesForProduct(productId);
        // Find the first active recurring price (subscription)
        const subscriptionPrice = prices.find(
          (price: StripePrice) => price.active && price.type === "recurring",
        );

        if (subscriptionPrice) {
          setFormData((prev) => ({
            ...prev,
            priceId: subscriptionPrice.id,
          }));
          toast.success("Configuration Stripe automatique réussie ✓");
        } else {
          toast.warning("Aucun prix d'abonnement actif trouvé pour ce produit");
        }
      } catch (error) {
        console.error("Error fetching prices for product:", error);
        toast.error("Erreur lors de la récupération des prix du produit");
      }
    }
  };

  const handleCreatePlan = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Filter out empty features
      const cleanedFeatures = formData.features.filter((f) => f.trim() !== "");

      const planData = {
        ...formData,
        features: cleanedFeatures,
        badge:
          formData.isPopular && formData.badge ? formData.badge : undefined,
        badgeColor:
          formData.isPopular && formData.badgeColor
            ? formData.badgeColor
            : undefined,
      };

      await dispatch(createPlan(planData)).unwrap();
      toast.success("Plan créé avec succès");
      onSuccess();
      onOpenChange(false);

      // Reset form
      setFormData({
        name: "",
        description: "",
        features: [""],
        billingInterval: "month",
        enabledFeatures: [],
        isActive: true,
        isPopular: false,
        badge: "",
        badgeColor: "",
        displayOrder: 1,
        productId: undefined,
        priceId: undefined,
      });
    } catch (error) {
      console.error("Error creating plan:", error);
      toast.error("Erreur lors de la création du plan");
    } finally {
      setLoading(false);
    }
  };

  const addFeature = () => {
    setFormData((prev) => ({
      ...prev,
      features: [...prev.features, ""],
    }));
  };

  const removeFeature = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index),
    }));
  };

  const updateFeature = (index: number, value: string) => {
    setFormData((prev) => ({
      ...prev,
      features: prev.features.map((f, i) => (i === index ? value : f)),
    }));
  };

  // Helper function to get feature icon
  const getFeatureIcon = (iconName: string) => {
    const iconProps = { className: "w-5 h-5 text-gray-500" };
    switch (iconName) {
      case "Code2":
        return <Code2 {...iconProps} />;
      case "LibraryBig":
        return <LibraryBig {...iconProps} />;
      case "AppWindowMac":
        return <AppWindowMac {...iconProps} />;
      case "Download":
        return <Download {...iconProps} />;
      default:
        return <Code2 {...iconProps} />;
    }
  };

  // Toggle feature on/off
  const toggleFeature = (featureId: string, enabled: boolean) => {
    setFormData((prev) => {
      const currentFeatures = prev.enabledFeatures || [];
      if (enabled) {
        return {
          ...prev,
          enabledFeatures: [...currentFeatures, featureId],
        };
      } else {
        return {
          ...prev,
          enabledFeatures: currentFeatures.filter((id) => id !== featureId),
        };
      }
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto scrollbar-thin">
        <DialogHeader>
          <DialogTitle>Créer un nouveau plan</DialogTitle>
          <DialogDescription>
            Configurez les détails de votre nouveau plan d'abonnement.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleCreatePlan} className="space-y-6">
          {/* Basic Information */}
          <div className="space-y-4">
            <div>
              <Label htmlFor="name">Nom du plan</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, name: e.target.value }))
                }
                required
              />
            </div>

            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
                required
              />
            </div>

            <div>
              <Label htmlFor="displayOrder">Ordre d'affichage</Label>
              <Input
                id="displayOrder"
                type="number"
                min="1"
                value={formData.displayOrder}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    displayOrder: parseInt(e.target.value) || 1,
                  }))
                }
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                Les plans avec un ordre plus petit apparaîtront en premier
              </p>
            </div>

            <div>
              <Label htmlFor="billingInterval">Intervalle de facturation</Label>
              <Select
                value={formData.billingInterval || "month"}
                onValueChange={(value: "month" | "year") =>
                  setFormData((prev) => ({ ...prev, billingInterval: value }))
                }
              >
                <SelectTrigger id="billingInterval">
                  <SelectValue placeholder="Sélectionnez l'intervalle" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="month">Mensuel</SelectItem>
                  <SelectItem value="year">Annuel</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-1">
                Définit si le plan est facturé mensuellement ou annuellement
              </p>
            </div>

            <div>
              <Label htmlFor="discountPercentage">
                Pourcentage de réduction (%)
              </Label>
              <Input
                id="discountPercentage"
                type="number"
                min="0"
                max="100"
                value={formData.discountPercentage ?? ""}
                onChange={(e) => {
                  const value = e.target.value;
                  setFormData((prev) => ({
                    ...prev,
                    discountPercentage: value === "" ? null : parseInt(value),
                  }));
                }}
                placeholder="0"
              />
              <p className="text-xs text-gray-500 mt-1">
                Réduction appliquée au prix du plan (0-100%). Laissez vide pour
                aucune réduction.
              </p>
            </div>

            <div>
              <Label htmlFor="productId">Produit Stripe associé</Label>
              <Select
                value={formData.productId || ""}
                onValueChange={(value) => handleProductChange(value)}
                required
                disabled={
                  productsLoading ||
                  plansLoading ||
                  availableProducts.length === 0
                }
              >
                <SelectTrigger id="productId">
                  <SelectValue
                    placeholder={
                      productsLoading || plansLoading
                        ? "Chargement..."
                        : "Sélectionnez un produit Stripe"
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {availableProducts.map((product) => (
                    <SelectItem key={product.id} value={product.id}>
                      {product.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-1">
                Associez ce plan à un produit Stripe (ex: Starter, Pro...)
              </p>
              {availableProducts.length === 0 &&
                !productsLoading &&
                !plansLoading && (
                  <div className="mt-4 flex items-center gap-2 bg-yellow-50 border border-yellow-300 text-yellow-800 rounded px-3 py-2">
                    <div>
                      <AlertTriangle className="w-5 h-5 text-yellow-500" />
                    </div>
                    <span>
                      Aucun produit Stripe disponible. Veuillez d'abord créer un
                      nouveau produit sur Stripe avant de créer un plan, pour
                      pouvoir l'associer à ce plan.
                    </span>
                  </div>
                )}
            </div>
          </div>

          <Separator />

          {/* Features */}
          <div className="space-y-4">
            <Label>Fonctionnalités</Label>
            {formData.features.map((feature, index) => (
              <div key={index} className="flex items-center space-x-2">
                <Input
                  value={feature}
                  onChange={(e) => updateFeature(index, e.target.value)}
                  placeholder="Fonctionnalité..."
                />
                {formData.features.length > 1 && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => removeFeature(index)}
                  >
                    <Minus className="w-4 h-4" />
                  </Button>
                )}
              </div>
            ))}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={addFeature}
            >
              <Plus className="w-4 h-4 mr-2" />
              Ajouter une fonctionnalité
            </Button>
          </div>

          <Separator />

          {/* Plan Features Configuration */}
          <div className="space-y-4">
            <Label>Configuration des fonctionnalités du plan</Label>
            <p className="text-sm text-gray-500">
              Activez ou désactivez les fonctionnalités disponibles pour ce plan
            </p>

            {/* Core Features */}
            <div className="space-y-3">
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Fonctionnalités principales
              </h4>
              {getFeaturesByCategory("core").map((feature) => (
                <div
                  key={feature.id}
                  className="flex items-center justify-between p-3 border rounded-lg"
                >
                  <div className="flex items-center space-x-3">
                    {getFeatureIcon(feature.icon)}
                    <div>
                      <p className="font-medium text-sm">{feature.name}</p>
                      <p className="text-xs text-gray-500">
                        {feature.description}
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={
                      formData.enabledFeatures?.includes(feature.id) || false
                    }
                    onCheckedChange={(checked) =>
                      toggleFeature(feature.id, checked)
                    }
                  />
                </div>
              ))}
            </div>

            {/* Premium Features */}
            <div className="space-y-3">
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Fonctionnalités premium
              </h4>
              {getFeaturesByCategory("premium").map((feature) => (
                <div
                  key={feature.id}
                  className="flex items-center justify-between p-3 border rounded-lg"
                >
                  <div className="flex items-center space-x-3">
                    {getFeatureIcon(feature.icon)}
                    <div>
                      <p className="font-medium text-sm">{feature.name}</p>
                      <p className="text-xs text-gray-500">
                        {feature.description}
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={
                      formData.enabledFeatures?.includes(feature.id) || false
                    }
                    onCheckedChange={(checked) =>
                      toggleFeature(feature.id, checked)
                    }
                  />
                </div>
              ))}
            </div>
          </div>

          <Separator />

          {/* Settings */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label>Plan actif</Label>
                <p className="text-sm text-gray-500">
                  Le plan sera visible aux utilisateurs
                </p>
              </div>
              <Switch
                checked={formData.isActive}
                onCheckedChange={(checked) =>
                  setFormData((prev) => ({ ...prev, isActive: checked }))
                }
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <Label>Plan populaire</Label>
                <p className="text-sm text-gray-500">
                  Marquer comme plan recommandé
                </p>
              </div>
              <Switch
                checked={formData.isPopular}
                onCheckedChange={(checked) =>
                  setFormData((prev) => ({ ...prev, isPopular: checked }))
                }
              />
            </div>

            {formData.isPopular && (
              <div className="grid grid-cols-2 gap-4 ml-4">
                <div>
                  <Label htmlFor="badge">Texte du badge</Label>
                  <Input
                    id="badge"
                    value={formData.badge}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        badge: e.target.value,
                      }))
                    }
                    placeholder="Le plus populaire"
                  />
                </div>
                <div>
                  <Label htmlFor="badgeColor">Couleur du badge</Label>
                  <Input
                    id="badgeColor"
                    value={formData.badgeColor}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        badgeColor: e.target.value,
                      }))
                    }
                    placeholder="bg-gradient-to-r from-violet-500 to-purple-500"
                  />
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Annuler
            </Button>
            <Button
              type="submit"
              disabled={loading || availableProducts.length === 0}
            >
              {loading ? "Création..." : "Créer le plan"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Edit Plan Dialog
interface EditPlanDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  plan: AdminPlan | null;
  onSuccess: () => void;
}

export const EditPlanDialog: React.FC<EditPlanDialogProps> = ({
  open,
  onOpenChange,
  plan,
  onSuccess,
}) => {
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<AdminCreatePlanRequest>({
    name: "",
    description: "",
    features: [""],
    billingInterval: "month",
    discountPercentage: null,
    enabledFeatures: [],
    isActive: true,
    isPopular: false,
    badge: "",
    badgeColor: "",
    displayOrder: 1,
    priceId: undefined,
  });

  useEffect(() => {
    if (plan) {
      setFormData({
        name: plan.name,
        description: plan.description,
        features: plan.features.length > 0 ? plan.features : [""],
        billingInterval: plan.billingInterval || "month",
        discountPercentage: plan.discountPercentage ?? null,
        enabledFeatures: plan.enabledFeatures || [],
        isActive: plan.isActive,
        isPopular: plan.isPopular,
        badge: plan.badge || "",
        badgeColor: plan.badgeColor || "",
        displayOrder: plan.displayOrder,
        priceId: plan.priceId || "",
      });
    }
  }, [plan]);

  const handleUpdatePlan = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!plan) return;

    setLoading(true);

    try {
      const cleanedFeatures = formData.features.filter((f) => f.trim() !== "");

      const planData = {
        ...formData,
        features: cleanedFeatures,
        badge:
          formData.isPopular && formData.badge ? formData.badge : undefined,
        badgeColor:
          formData.isPopular && formData.badgeColor
            ? formData.badgeColor
            : undefined,
      };

      await dispatch(updatePlan({ id: plan.id, data: planData })).unwrap();
      toast.success("Plan mis à jour avec succès");
      onSuccess();
      onOpenChange(false);
    } catch (error) {
      console.error("Error updating plan:", error);
      toast.error("Erreur lors de la mise à jour du plan");
    } finally {
      setLoading(false);
    }
  };

  const addFeature = () => {
    setFormData((prev) => ({
      ...prev,
      features: [...prev.features, ""],
    }));
  };

  const removeFeature = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index),
    }));
  };

  const updateFeature = (index: number, value: string) => {
    setFormData((prev) => ({
      ...prev,
      features: prev.features.map((f, i) => (i === index ? value : f)),
    }));
  };

  // Helper function to get feature icon
  const getFeatureIcon = (iconName: string) => {
    const iconProps = { className: "w-5 h-5 text-gray-500" };
    switch (iconName) {
      case "Code2":
        return <Code2 {...iconProps} />;
      case "LibraryBig":
        return <LibraryBig {...iconProps} />;
      case "AppWindowMac":
        return <AppWindowMac {...iconProps} />;
      case "Download":
        return <Download {...iconProps} />;
      default:
        return <Code2 {...iconProps} />;
    }
  };

  // Toggle feature on/off
  const toggleFeature = (featureId: string, enabled: boolean) => {
    setFormData((prev) => {
      const currentFeatures = prev.enabledFeatures || [];
      if (enabled) {
        return {
          ...prev,
          enabledFeatures: [...currentFeatures, featureId],
        };
      } else {
        return {
          ...prev,
          enabledFeatures: currentFeatures.filter((id) => id !== featureId),
        };
      }
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto scrollbar-thin">
        <DialogHeader>
          <DialogTitle>Modifier le plan</DialogTitle>

          <DialogDescription>
            Modifiez les informations du plan d'abonnement
          </DialogDescription>
        </DialogHeader>
        {plan && (
          <div className="text-sm text-gray-500 mt-1">
            Produit Stripe associé : {plan.name || "Aucun"}{" "}
            <span className="font-mono">({plan.productId || "Aucun"})</span>
          </div>
        )}
        {plan && (
          <div className="text-sm text-gray-500 mt-1">
            Price ID Stripe associé : {plan.priceId || "Aucun"}
          </div>
        )}

        <form onSubmit={handleUpdatePlan} className="space-y-6">
          {/* Same form content as CreatePlanDialog */}
          <div className="space-y-4">
            <div>
              <Label htmlFor="name">Nom du plan</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, name: e.target.value }))
                }
                required
              />
            </div>

            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
                required
              />
            </div>

            <div>
              <Label htmlFor="displayOrder">Ordre d'affichage</Label>
              <Input
                id="displayOrder"
                type="number"
                min="1"
                value={formData.displayOrder}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    displayOrder: parseInt(e.target.value) || 1,
                  }))
                }
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                Les plans avec un ordre plus petit apparaîtront en premier
              </p>
            </div>

            <div>
              <Label htmlFor="billingInterval-edit">
                Intervalle de facturation
              </Label>
              <Select
                value={formData.billingInterval || "month"}
                onValueChange={(value: "month" | "year") =>
                  setFormData((prev) => ({ ...prev, billingInterval: value }))
                }
              >
                <SelectTrigger id="billingInterval-edit">
                  <SelectValue placeholder="Sélectionnez l'intervalle" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="month">Mensuel</SelectItem>
                  <SelectItem value="year">Annuel</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-1">
                Définit si le plan est facturé mensuellement ou annuellement
              </p>
            </div>

            <div>
              <Label htmlFor="discountPercentage-edit">
                Pourcentage de réduction (%)
              </Label>
              <Input
                id="discountPercentage-edit"
                type="number"
                min="0"
                max="100"
                value={formData.discountPercentage ?? ""}
                onChange={(e) => {
                  const value = e.target.value;
                  setFormData((prev) => ({
                    ...prev,
                    discountPercentage: value === "" ? null : parseInt(value),
                  }));
                }}
                placeholder="0"
              />
              <p className="text-xs text-gray-500 mt-1">
                Réduction appliquée au prix du plan (0-100%). Laissez vide pour
                aucune réduction.
              </p>
            </div>
          </div>

          <Separator />

          <div className="space-y-4">
            <Label>Fonctionnalités</Label>
            {formData.features.map((feature, index) => (
              <div key={index} className="flex items-center space-x-2">
                <Input
                  value={feature}
                  onChange={(e) => updateFeature(index, e.target.value)}
                  placeholder="Fonctionnalité..."
                />
                {formData.features.length > 1 && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => removeFeature(index)}
                  >
                    <Minus className="w-4 h-4" />
                  </Button>
                )}
              </div>
            ))}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={addFeature}
            >
              <Plus className="w-4 h-4 mr-2" />
              Ajouter une fonctionnalité
            </Button>
          </div>

          <Separator />

          {/* Plan Features Configuration */}
          <div className="space-y-4">
            <Label>Configuration des fonctionnalités du plan</Label>
            <p className="text-sm text-gray-500">
              Activez ou désactivez les fonctionnalités disponibles pour ce plan
            </p>

            {/* Core Features */}
            <div className="space-y-3">
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Fonctionnalités principales
              </h4>
              {getFeaturesByCategory("core").map((feature) => (
                <div
                  key={feature.id}
                  className="flex items-center justify-between p-3 border rounded-lg"
                >
                  <div className="flex items-center space-x-3">
                    {getFeatureIcon(feature.icon)}
                    <div>
                      <p className="font-medium text-sm">{feature.name}</p>
                      <p className="text-xs text-gray-500">
                        {feature.description}
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={
                      formData.enabledFeatures?.includes(feature.id) || false
                    }
                    onCheckedChange={(checked) =>
                      toggleFeature(feature.id, checked)
                    }
                  />
                </div>
              ))}
            </div>

            {/* Premium Features */}
            <div className="space-y-3">
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Fonctionnalités premium
              </h4>
              {getFeaturesByCategory("premium").map((feature) => (
                <div
                  key={feature.id}
                  className="flex items-center justify-between p-3 border rounded-lg"
                >
                  <div className="flex items-center space-x-3">
                    {getFeatureIcon(feature.icon)}
                    <div>
                      <p className="font-medium text-sm">{feature.name}</p>
                      <p className="text-xs text-gray-500">
                        {feature.description}
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={
                      formData.enabledFeatures?.includes(feature.id) || false
                    }
                    onCheckedChange={(checked) =>
                      toggleFeature(feature.id, checked)
                    }
                  />
                </div>
              ))}
            </div>
          </div>

          <Separator />

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label>Plan actif</Label>
                <p className="text-sm text-gray-500">
                  Le plan sera visible aux utilisateurs
                </p>
              </div>
              <Switch
                checked={formData.isActive}
                onCheckedChange={(checked) =>
                  setFormData((prev) => ({ ...prev, isActive: checked }))
                }
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <Label>Plan populaire</Label>
                <p className="text-sm text-gray-500">
                  Marquer comme plan recommandé
                </p>
              </div>
              <Switch
                checked={formData.isPopular}
                onCheckedChange={(checked) =>
                  setFormData((prev) => ({ ...prev, isPopular: checked }))
                }
              />
            </div>

            {formData.isPopular && (
              <div className="grid grid-cols-2 gap-4 ml-4">
                <div>
                  <Label htmlFor="badge">Texte du badge</Label>
                  <Input
                    id="badge"
                    value={formData.badge}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        badge: e.target.value,
                      }))
                    }
                    placeholder="Le plus populaire"
                  />
                </div>
                <div>
                  <Label htmlFor="badgeColor">Couleur du badge</Label>
                  <Input
                    id="badgeColor"
                    value={formData.badgeColor}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        badgeColor: e.target.value,
                      }))
                    }
                    placeholder="bg-gradient-to-r from-violet-500 to-purple-500"
                  />
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Annuler
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? "Mise à jour..." : "Mettre à jour"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// View Plan Dialog
interface ViewPlanDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  plan: AdminPlan | null;
}

export const ViewPlanDialog: React.FC<ViewPlanDialogProps> = ({
  open,
  onOpenChange,
  plan,
}) => {
  if (!plan) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <div className="flex items-center space-x-3">
            <DialogTitle className="text-2xl">{plan.name}</DialogTitle>
            {plan.isPopular && plan.badge && (
              <Badge className="bg-gradient-to-r from-violet-500 to-purple-500 text-white">
                <Star className="w-3 h-3 mr-1" />
                {plan.badge}
              </Badge>
            )}
            <Badge variant={plan.isActive ? "default" : "secondary"}>
              {plan.isActive ? "Actif" : "Inactif"}
            </Badge>
          </div>

          <DialogDescription className="text-lg">
            {plan.description}
          </DialogDescription>
        </DialogHeader>
        <div className="text-sm text-gray-500 mt-1">
          Produit Stripe associé : {plan.name || "Aucun"}{" "}
          <span className="font-mono">({plan.productId || "Aucun"})</span>
        </div>
        {plan && (
          <div className="text-sm text-gray-500 mt-1">
            Price ID Stripe associé : {plan.priceId || "Aucun"}
          </div>
        )}

        <div className="space-y-6">
          {/* Price */}
          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
            <h3 className="text-lg font-semibold mb-2">Prix</h3>
            {plan.discountPercentage && plan.discountPercentage > 0 ? (
              <>
                {/* Prix original barré */}
                <div className="text-lg text-gray-400 dark:text-gray-500 line-through mb-1">
                  {plan.currency}
                  {(
                    Number(plan.price) /
                    (1 - Number(plan.discountPercentage) / 100)
                  ).toFixed(2)}
                  <span className="text-sm font-normal ml-2">
                    HT /{plan.billingInterval === "year" ? "an" : "mois"}
                  </span>
                </div>
                {/* Prix avec réduction (déjà appliquée) */}
                <div className="text-3xl font-bold text-violet-600 dark:text-violet-400">
                  {plan.currency}
                  {Number(plan.price).toFixed(2)}
                  <span className="text-lg font-normal text-gray-500 ml-2">
                    HT /{plan.billingInterval === "year" ? "an" : "mois"}
                  </span>
                  <span className="text-lg font-semibold text-green-600 dark:text-green-400 ml-2">
                    (-{plan.discountPercentage}%)
                  </span>
                </div>
                {plan.billingInterval === "year" && (
                  <p className="text-sm text-gray-500 mt-2">
                    Soit {(Number(plan.price) / 12).toFixed(2)} {plan.currency}{" "}
                    HT/mois
                  </p>
                )}
              </>
            ) : (
              <>
                <div className="text-3xl font-bold text-violet-600 dark:text-violet-400">
                  {plan.currency}
                  {Number(plan.price).toFixed(2)}
                  <span className="text-lg font-normal text-gray-500 ml-2">
                    HT /{plan.billingInterval === "year" ? "an" : "mois"}
                  </span>
                </div>
                {plan.billingInterval === "year" && (
                  <p className="text-sm text-gray-500 mt-2">
                    Soit {(Number(plan.price) / 12).toFixed(2)} {plan.currency}{" "}
                    HT/mois
                  </p>
                )}
              </>
            )}
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
              <span className="font-medium">Facturation :</span>{" "}
              {plan.billingInterval === "year" ? "Annuelle" : "Mensuelle"}
            </p>
            {plan.discountPercentage !== null &&
              plan.discountPercentage !== undefined &&
              plan.discountPercentage > 0 && (
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                  <span className="font-medium">Réduction appliquée :</span>{" "}
                  <span className="text-green-600 dark:text-green-400 font-semibold">
                    {plan.discountPercentage}%
                  </span>
                </p>
              )}
          </div>

          {/* Features */}
          <div>
            <h3 className="text-lg font-semibold mb-3">Fonctionnalités</h3>
            <ul className="space-y-2">
              {plan.features.map((feature: string, index: number) => (
                <li key={index} className="flex items-center space-x-2">
                  <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Metadata */}
          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
            <h3 className="text-lg font-semibold mb-3">Informations</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="font-medium">Créé le:</span>
                <p className="text-gray-600 dark:text-gray-400">
                  {new Date(plan.createdAt).toLocaleDateString()}
                </p>
              </div>
              <div>
                <span className="font-medium">Modifié le:</span>
                <p className="text-gray-600 dark:text-gray-400">
                  {new Date(plan.updatedAt).toLocaleDateString()}
                </p>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Fermer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Delete Plan Dialog
interface DeletePlanDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  plan: AdminPlan | null;
  onSuccess: () => void;
}

export const DeletePlanDialog: React.FC<DeletePlanDialogProps> = ({
  open,
  onOpenChange,
  plan,
  onSuccess,
}) => {
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(false);

  const handleDelete = async () => {
    if (!plan) return;

    setLoading(true);

    try {
      await dispatch(deletePlan(plan.id)).unwrap();
      toast.success("Plan supprimé avec succès");
      onSuccess();
      onOpenChange(false);
    } catch (error) {
      console.error("Error deleting plan:", error);
      toast.error("Erreur lors de la suppression du plan");
    } finally {
      setLoading(false);
    }
  };

  if (!plan) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Supprimer le plan</DialogTitle>
          <DialogDescription>
            Êtes-vous sûr de vouloir supprimer le plan "{plan.name}" ? Cette
            action est irréversible.
          </DialogDescription>
        </DialogHeader>

        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <h4 className="font-medium text-red-800 dark:text-red-200 mb-2">
            Attention
          </h4>
          <p className="text-red-700 dark:text-red-300 text-sm">
            La suppression de ce plan affectera tous les utilisateurs
            actuellement abonnés. Assurez-vous de les migrer vers un autre plan
            ou de les notifier avant de procéder.
          </p>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Annuler
          </Button>
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={loading}
          >
            {loading ? "Suppression..." : "Supprimer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
